import { autoTolerance } from './core/mesh';
import { writeBinaryStl } from './core/stl';
import {
  defaultRepairOptions,
  type MeshData,
  type MeshStats,
  type RepairOptions,
  type RepairReport,
} from './core/types';
import type { RepairResponse } from './repair.worker';
import { getLang, onLangChange, setLang, t } from './i18n';
import {
  button,
  el,
  fmtArea,
  fmtBytes,
  fmtInt,
  fmtNum,
  fmtVolume,
  numberInput,
  section,
} from './ui';
import { Viewer, type ViewDirection } from './viewer';

type MeshView = 'repaired' | 'original';

interface EdgeData {
  open: Float32Array;
  nonManifold: Float32Array;
}

interface PendingRequest {
  resolve: (response: RepairResponse) => void;
  reject: (error: Error) => void;
}

interface StatusInfo {
  key: string;
  cls: 'ok' | 'warn' | 'bad' | 'muted';
}

function statusOf(stats: MeshStats | undefined): StatusInfo {
  if (!stats || stats.triangles === 0) return { key: 'statusEmpty', cls: 'muted' };
  if (stats.watertight) return { key: 'statusWatertight', cls: 'ok' };
  if (stats.closed) return { key: 'statusClosedNonManifold', cls: 'warn' };
  return { key: 'statusOpen', cls: 'bad' };
}

function emptyEdges(): EdgeData {
  return { open: new Float32Array(0), nonManifold: new Float32Array(0) };
}

export class App {
  private viewer: Viewer;
  private panel: HTMLElement;

  private busyOverlay: HTMLElement;
  private busyText: HTMLElement;
  private dropOverlay: HTMLElement;
  private healthBadge: HTMLElement;

  private modelInfoEl: HTMLElement | null = null;
  private statsEl: HTMLElement | null = null;
  private reportEl: HTMLElement | null = null;
  private repairStatusEl: HTMLElement | null = null;
  private toleranceHintEl: HTMLElement | null = null;
  private repairBtn: HTMLButtonElement | null = null;
  private downloadBtn: HTMLButtonElement | null = null;
  private exportNameInput: HTMLInputElement | null = null;
  private fileInput: HTMLInputElement | null = null;

  private fileBuffer: ArrayBuffer | null = null;
  private fileName = '';
  private fileSize = 0;
  private format: 'binary' | 'ascii' = 'binary';
  private exportName = 'model_fixed.stl';

  private original: MeshData | null = null;
  private repaired: MeshData | null = null;
  private originalEdges: EdgeData = emptyEdges();
  private repairedEdges: EdgeData = emptyEdges();
  private report: RepairReport | null = null;

  private options: RepairOptions = defaultRepairOptions();
  private view: MeshView = 'repaired';
  private busy = false;
  private busyKey = 'busyDefault';
  private settingsDirty = false;

  private worker: Worker | null = null;
  private pending = new Map<number, PendingRequest>();
  private requestSeq = 0;
  private viewButtons: HTMLButtonElement[] = [];

  constructor(panel: HTMLElement, viewport: HTMLElement) {
    this.panel = panel;
    this.busyOverlay = document.getElementById('busy-overlay')!;
    this.busyText = document.getElementById('busy-text')!;
    this.dropOverlay = document.getElementById('drop-overlay')!;
    this.healthBadge = document.getElementById('health-badge')!;

    this.viewer = new Viewer(viewport);
    this.bindViewerControls();

    this.buildUI();
    this.bindDropZone(viewport);
    this.bindKeys();

    onLangChange(() => {
      this.buildUI();
      this.applyStaticText();
      this.applyOverlayText();
    });
    this.applyStaticText();
    this.applyOverlayText();
  }

  // ---------- UI ----------

  private bindViewerControls(): void {
    this.viewButtons = Array.from(
      document.querySelectorAll<HTMLButtonElement>('#view-toggle button[data-view]'),
    );
    for (const buttonEl of this.viewButtons) {
      buttonEl.addEventListener('click', () => {
        this.setView(buttonEl.dataset.view === 'original' ? 'original' : 'repaired');
      });
    }

    const wire = document.getElementById('show-wireframe') as HTMLInputElement | null;
    wire?.addEventListener('change', () => this.viewer.setWireframe(!!wire.checked));

    const issues = document.getElementById('show-issues') as HTMLInputElement | null;
    issues?.addEventListener('change', () => this.viewer.setIssues(!!issues.checked));

    document.querySelectorAll<HTMLButtonElement>('button[data-view-dir]').forEach((buttonEl) => {
      buttonEl.addEventListener('click', () => {
        const direction = (buttonEl.dataset.viewDir ?? 'iso') as ViewDirection;
        this.viewer.setViewDirection(direction);
      });
    });
  }

  private bindKeys(): void {
    window.addEventListener('keydown', (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'o') {
        event.preventDefault();
        this.fileInput?.click();
      }
    });
  }

  private bindDropZone(viewport: HTMLElement): void {
    const show = (): void => this.dropOverlay.classList.add('show');
    const hide = (): void => this.dropOverlay.classList.remove('show');

    viewport.addEventListener('dragover', (event) => {
      event.preventDefault();
      show();
    });
    viewport.addEventListener('dragleave', (event) => {
      if (event.target === viewport) hide();
    });
    viewport.addEventListener('drop', (event) => {
      event.preventDefault();
      hide();
      const file = event.dataTransfer?.files?.[0];
      if (file) void this.loadFile(file);
    });
  }

  private buildUI(): void {
    this.panel.innerHTML = '';
    this.modelInfoEl = null;
    this.statsEl = null;
    this.reportEl = null;
    this.repairStatusEl = null;
    this.toleranceHintEl = null;
    this.repairBtn = null;
    this.downloadBtn = null;
    this.exportNameInput = null;
    this.fileInput = null;

    const langButton = button(getLang().toUpperCase(), {
      class: 'small lang-btn',
      title: t('langTitle'),
      onClick: () => setLang(getLang() === 'pl' ? 'en' : 'pl'),
    });

    const head = el('div', { class: 'panel-head' }, [
      el('div', {}, [
        el('h1', { text: 'Mesh Repair' }),
        el('p', { class: 'subtitle', text: t('subtitle') }),
      ]),
      langButton,
    ]);
    this.panel.append(head);

    this.buildModelSection();
    this.buildStatsSection();
    this.buildRepairSection();
    this.buildExportSection();
    this.buildHelpSection();

    this.applyStaticText();
    this.renderModelInfo();
    this.renderStats();
    this.renderReport();
    this.updateToleranceHint();
    this.renderRepairStatus();
    this.updateButtons();
    this.updateViewButtons();
    this.updateHealthBadge();
  }

  private buildModelSection(): void {
    const sec = section(t('modelTitle'), t('modelTag'), 'model', true);

    const fileInput = el('input', {
      type: 'file',
      accept: '.stl,model/stl,application/sla',
      style: 'display:none',
    }) as HTMLInputElement;
    fileInput.onchange = () => {
      const file = fileInput.files?.[0];
      if (file) void this.loadFile(file);
      fileInput.value = '';
    };
    this.fileInput = fileInput;

    const importBtn = button(t('importBtn'), {
      class: 'primary full',
      onClick: () => fileInput.click(),
    });

    this.modelInfoEl = el('div', { class: 'mt6' });
    sec.body.append(importBtn, fileInput, this.modelInfoEl);
    this.panel.append(sec.root);
  }

  private buildStatsSection(): void {
    const sec = section(t('statsTitle'), t('statsTag'), 'stats', true);
    this.statsEl = el('div', { class: 'stats' });
    const legend = el('div', { class: 'legend' }, [
      el('span', { class: 'legend-item' }, [
        el('span', { class: 'legend-dot open' }),
        el('span', { text: t('legendOpen') }),
      ]),
      el('span', { class: 'legend-item' }, [
        el('span', { class: 'legend-dot nonmanifold' }),
        el('span', { text: t('legendNonManifold') }),
      ]),
    ]);
    sec.body.append(this.statsEl, legend);
    this.panel.append(sec.root);
  }

  private buildRepairSection(): void {
    const sec = section(t('repairTitle'), t('repairTag'), 'repair', true);

    const tolerance = numberInput(this.options.tolerance, {
      min: 0,
      step: 0.001,
      width: '100px',
    });
    tolerance.oninput = () => {
      const value = Number(tolerance.value);
      this.options.tolerance = Number.isFinite(value) && value > 0 ? value : 0;
      this.markDirty();
      this.updateToleranceHint();
    };

    this.toleranceHintEl = el('div', { class: 'hint-block' });

    const maxHole = numberInput(this.options.maxHoleEdges, {
      min: 0,
      step: 1,
      width: '100px',
    });
    maxHole.oninput = () => {
      const value = Math.round(Number(maxHole.value));
      this.options.maxHoleEdges = Number.isFinite(value) && value > 0 ? value : 0;
      this.markDirty();
    };

    const checks = el('div', { class: 'checks' }, [
      this.checkbox(t('optWeld'), this.options.weld, (value) => {
        this.options.weld = value;
        this.markDirty();
      }),
      this.checkbox(t('optDegenerate'), this.options.removeDegenerate, (value) => {
        this.options.removeDegenerate = value;
        this.markDirty();
      }),
      this.checkbox(t('optDuplicates'), this.options.removeDuplicates, (value) => {
        this.options.removeDuplicates = value;
        this.markDirty();
      }),
      this.checkbox(t('optWinding'), this.options.fixWinding, (value) => {
        this.options.fixWinding = value;
        this.markDirty();
      }),
      this.checkbox(t('optFill'), this.options.fillHoles, (value) => {
        this.options.fillHoles = value;
        this.markDirty();
      }),
      this.checkbox(t('optLargest'), this.options.keepLargestShell, (value) => {
        this.options.keepLargestShell = value;
        this.markDirty();
      }),
    ]);

    this.repairBtn = button(t('repairRunAgain'), {
      class: 'primary full mt6',
      onClick: () => void this.runRepair(),
    });
    this.repairStatusEl = el('div', { class: 'repair-status' });
    this.reportEl = el('div', { class: 'report' });

    sec.body.append(
      el('div', { class: 'row' }, [
        el('label', { class: 'big', text: t('toleranceLabel') }),
        tolerance,
      ]),
      this.toleranceHintEl,
      el('div', { class: 'row mt6' }, [
        el('label', { class: 'big', text: t('maxHoleLabel') }),
        maxHole,
      ]),
      el('div', { class: 'hint-block', text: t('maxHoleHint') }),
      checks,
      this.repairBtn,
      this.repairStatusEl,
      this.reportEl,
    );
    this.panel.append(sec.root);
  }

  private buildExportSection(): void {
    const sec = section(t('exportTitle'), t('exportTag'), 'export', true);

    const nameInput = el('input', {
      type: 'text',
      value: this.exportName,
    }) as HTMLInputElement;
    nameInput.oninput = () => {
      this.exportName = nameInput.value;
    };
    this.exportNameInput = nameInput;

    this.downloadBtn = button(t('downloadBtn'), {
      class: 'primary full',
      onClick: () => this.download(),
    });

    sec.body.append(
      el('label', { class: 'field' }, [
        el('span', { text: t('exportNameLabel') }),
        nameInput,
      ]),
      this.downloadBtn,
      el('div', { class: 'hint-block', text: t('exportHint') }),
    );
    this.panel.append(sec.root);
  }

  private buildHelpSection(): void {
    const sec = section(t('helpTitle'), t('helpTag'), 'help', false);
    sec.body.append(el('p', { class: 'hint-block', text: t('helpText') }));
    this.panel.append(sec.root);
  }

  private checkbox(
    label: string,
    checked: boolean,
    onChange: (value: boolean) => void,
  ): HTMLElement {
    const input = el('input', { type: 'checkbox' }) as HTMLInputElement;
    input.checked = checked;
    input.addEventListener('change', () => onChange(input.checked));
    return el('label', { class: 'label-check' }, [input, el('span', { text: label })]);
  }

  private applyStaticText(): void {
    document.documentElement.lang = getLang();
    document.title = t('docTitle');

    const repairedBtn = document.getElementById('view-repaired');
    const originalBtn = document.getElementById('view-original');
    if (repairedBtn) repairedBtn.textContent = t('viewerRepaired');
    if (originalBtn) originalBtn.textContent = t('viewerOriginal');

    const wireLabel = document.getElementById('wire-label');
    if (wireLabel) wireLabel.textContent = t('viewerWireframe');
    const issuesLabel = document.getElementById('issues-label');
    if (issuesLabel) issuesLabel.textContent = t('viewerIssues');

    const hint = document.getElementById('viewer-hint');
    if (hint) hint.textContent = t('viewerHint');
  }

  private applyOverlayText(): void {
    const dropBox = this.dropOverlay.querySelector('.drop-box');
    if (dropBox) dropBox.textContent = t('dropHint');
    this.busyText.textContent = t(this.busyKey);
  }

  // ---------- render ----------

  private renderModelInfo(): void {
    const host = this.modelInfoEl;
    if (!host) return;
    host.innerHTML = '';

    if (!this.original) {
      host.append(el('div', { class: 'empty', text: t('noModel') }));
      return;
    }

    const before = this.report?.before;
    const dimensions = before
      ? `${fmtNum(before.bounds.size[0], 2)} × ${fmtNum(before.bounds.size[1], 2)} × ${fmtNum(before.bounds.size[2], 2)} mm`
      : '—';
    const rows: Array<[string, string]> = [
      [t('fileLabel'), this.fileName],
      [t('formatLabel'), this.format === 'binary' ? t('formatBinary') : t('formatAscii')],
      [t('fileSizeLabel'), fmtBytes(this.fileSize)],
      [t('rowTriangles'), fmtInt(before?.triangles ?? this.original.indices.length / 3)],
      [t('dimensionsLabel'), dimensions],
    ];

    for (const [label, value] of rows) {
      host.append(
        el('div', { class: 'stat' }, [
          el('span', { text: label }),
          el('b', { class: 'mono ellipsis', title: value, text: value }),
        ]),
      );
    }
  }

  private renderStats(): void {
    const host = this.statsEl;
    if (!host) return;
    host.innerHTML = '';

    if (!this.report) {
      host.append(el('div', { class: 'empty', text: t('statsEmpty') }));
      return;
    }

    const before = this.report.before;
    const after = this.report.after;
    const table = el('table', { class: 'stats-table' });
    const thead = el('thead', {}, [
      el('tr', {}, [
        el('th', {}),
        el('th', { text: t('colBefore') }),
        el('th', { text: t('colAfter') }),
      ]),
    ]);
    const tbody = el('tbody');

    const add = (label: string, b: string, a: string, warnBefore = false, warnAfter = false): void => {
      tbody.append(
        el('tr', {}, [
          el('th', { text: label }),
          el('td', { class: warnBefore ? 'bad' : '', text: b }),
          el('td', { class: warnAfter ? 'bad' : '', text: a }),
        ]),
      );
    };

    add(t('rowTriangles'), fmtInt(before.triangles), fmtInt(after.triangles));
    add(t('rowVertices'), fmtInt(before.vertices), fmtInt(after.vertices));
    add(t('rowEdges'), fmtInt(before.edges), fmtInt(after.edges));
    add(t('rowOpen'), fmtInt(before.openEdges), fmtInt(after.openEdges), before.openEdges > 0, after.openEdges > 0);
    add(t('rowHoles'), fmtInt(before.boundaryLoops), fmtInt(after.boundaryLoops), before.boundaryLoops > 0, after.boundaryLoops > 0);
    add(
      t('rowNonManifold'),
      fmtInt(before.nonManifoldEdges),
      fmtInt(after.nonManifoldEdges),
      before.nonManifoldEdges > 0,
      after.nonManifoldEdges > 0,
    );
    add(
      t('rowInconsistent'),
      fmtInt(before.inconsistentEdges),
      fmtInt(after.inconsistentEdges),
      before.inconsistentEdges > 0,
      after.inconsistentEdges > 0,
    );
    add(
      t('rowDuplicates'),
      fmtInt(before.duplicateFaces),
      fmtInt(after.duplicateFaces),
      before.duplicateFaces > 0,
      after.duplicateFaces > 0,
    );
    add(
      t('rowDegenerate'),
      fmtInt(before.degenerateFaces),
      fmtInt(after.degenerateFaces),
      before.degenerateFaces > 0,
      after.degenerateFaces > 0,
    );
    add(t('rowShells'), fmtInt(before.shells), fmtInt(after.shells));
    add(t('rowVolume'), fmtVolume(before.volume), fmtVolume(after.volume));
    add(t('rowArea'), fmtArea(before.area), fmtArea(after.area));

    const beforeStatus = statusOf(before);
    const afterStatus = statusOf(after);
    tbody.append(
      el('tr', { class: 'status-row' }, [
        el('th', { text: t('rowStatus') }),
        el('td', { class: beforeStatus.cls, text: t(beforeStatus.key) }),
        el('td', { class: afterStatus.cls, text: t(afterStatus.key) }),
      ]),
    );

    table.append(thead, tbody);
    host.append(table);
  }

  private renderReport(): void {
    const host = this.reportEl;
    if (!host) return;
    host.innerHTML = '';

    if (!this.report) return;

    const report = this.report;
    const items: Array<[string, string, boolean]> = [];

    items.push([t('toleranceLabel'), `${fmtNum(report.tolerance, 4)} mm`, false]);
    if (report.weldedVertices > 0) items.push([t('reportWelded'), fmtInt(report.weldedVertices), false]);
    if (report.removedDegenerate > 0) items.push([t('reportDegenerate'), fmtInt(report.removedDegenerate), false]);
    if (report.removedDuplicates > 0) items.push([t('reportDuplicates'), fmtInt(report.removedDuplicates), false]);
    if (report.flippedFaces > 0) items.push([t('reportWinding'), fmtInt(report.flippedFaces), false]);
    if (report.filledHoles > 0) items.push([t('reportHoles'), fmtInt(report.filledHoles), false]);
    if (report.filledTriangles > 0) items.push([t('reportHoleTris'), fmtInt(report.filledTriangles), false]);
    if (report.skippedHoles > 0) items.push([t('reportSkippedHoles'), fmtInt(report.skippedHoles), true]);
    if (report.removedShells > 0) items.push([t('reportShells'), fmtInt(report.removedShells), false]);

    const changed =
      report.weldedVertices > 0 ||
      report.removedDegenerate > 0 ||
      report.removedDuplicates > 0 ||
      report.flippedFaces > 0 ||
      report.filledHoles > 0 ||
      report.removedShells > 0;

    host.append(el('h3', { text: t('reportTitle') }));
    if (!changed) host.append(el('div', { class: 'empty', text: t('reportNothing') }));

    for (const [label, value, warn] of items) {
      host.append(
        el('div', { class: `report-row${warn ? ' warn' : ''}` }, [
          el('span', { text: label }),
          el('b', { class: 'mono', text: value }),
        ]),
      );
    }

    host.append(
      el('div', { class: 'report-row subdued' }, [
        el('span', { text: t('reportTime') }),
        el('b', { class: 'mono', text: `${fmtNum(report.ms, 0)} ms` }),
      ]),
    );
  }

  private renderRepairStatus(): void {
    const host = this.repairStatusEl;
    if (!host) return;
    host.classList.remove('ok', 'warn', 'bad');

    if (!this.original) {
      host.textContent = '';
      return;
    }

    if (this.busy) {
      host.textContent = t(this.busyKey);
      return;
    }

    if (this.settingsDirty) {
      host.textContent = t('repairStatusStale');
      host.classList.add('warn');
      return;
    }

    const after = this.report?.after;
    if (!after) return;

    if (after.watertight) {
      host.textContent = t('repairStatusOk');
      host.classList.add('ok');
    } else if (after.openEdges > 0) {
      host.textContent = t('repairStatusRemaining', {
        open: fmtInt(after.openEdges),
        holes: fmtInt(after.boundaryLoops),
      });
      host.classList.add('bad');
    } else if (after.nonManifoldEdges > 0) {
      host.textContent = t('repairStatusNonManifold', { n: fmtInt(after.nonManifoldEdges) });
      host.classList.add('warn');
    } else {
      host.textContent = t('repairStatusOther');
      host.classList.add('warn');
    }
  }

  private updateToleranceHint(): void {
    if (!this.toleranceHintEl) return;
    const parts: string[] = [];
    if (this.original) {
      parts.push(t('toleranceAuto', { value: fmtNum(autoTolerance(this.original), 4) }));
    }
    parts.push(t('toleranceHint'));
    this.toleranceHintEl.textContent = parts.join(' ');
  }

  private updateButtons(): void {
    if (this.repairBtn) {
      this.repairBtn.textContent = this.report ? t('repairRunAgain') : t('repairBtn');
      this.repairBtn.disabled = this.busy || !this.fileBuffer;
    }
    if (this.downloadBtn) this.downloadBtn.disabled = this.busy || !this.repaired;
    if (this.exportNameInput && document.activeElement !== this.exportNameInput) {
      this.exportNameInput.value = this.exportName;
    }
    this.updateViewButtons();
  }

  private updateViewButtons(): void {
    for (const buttonEl of this.viewButtons) {
      const view = buttonEl.dataset.view === 'original' ? 'original' : 'repaired';
      buttonEl.classList.toggle('active', view === this.view);
      buttonEl.disabled = !this.original;
    }
  }

  private updateHealthBadge(): void {
    const stats = this.view === 'original' ? this.report?.before : this.report?.after;
    this.healthBadge.classList.remove('hidden', 'ok', 'warn', 'bad');

    if (!this.original || !stats) {
      this.healthBadge.classList.add('hidden');
      this.healthBadge.textContent = '';
      return;
    }

    if (stats.watertight) {
      this.healthBadge.classList.add('ok');
      this.healthBadge.textContent = `✓ ${t('badgeWatertight')}`;
    } else if (stats.closed) {
      this.healthBadge.classList.add('warn');
      this.healthBadge.textContent = `! ${t('badgeNonManifold')}`;
    } else {
      this.healthBadge.classList.add('bad');
      this.healthBadge.textContent = `✕ ${t('badgeOpen', { n: fmtInt(stats.openEdges) })}`;
    }
  }

  private renderAllAfterChange(): void {
    this.renderModelInfo();
    this.renderStats();
    this.renderReport();
    this.updateToleranceHint();
    this.renderRepairStatus();
    this.updateButtons();
    this.updateHealthBadge();
  }

  private markDirty(): void {
    if (!this.original) return;
    this.settingsDirty = true;
    this.renderRepairStatus();
  }

  // ---------- state ----------

  private setView(view: MeshView): void {
    if (view === 'original' && !this.original) return;
    this.view = view;
    this.showCurrentView(false);
    this.updateViewButtons();
    this.updateHealthBadge();
  }

  private showCurrentView(fit: boolean): void {
    if (!this.original) return;
    if (this.view === 'original') {
      this.viewer.setMesh(
        this.original,
        this.originalEdges.open,
        this.originalEdges.nonManifold,
        fit,
      );
      return;
    }
    if (this.repaired) {
      this.viewer.setMesh(
        this.repaired,
        this.repairedEdges.open,
        this.repairedEdges.nonManifold,
        fit,
      );
    }
  }

  private applyResponse(response: RepairResponse): void {
    if (
      !response.ok ||
      !response.original ||
      !response.repaired ||
      !response.report ||
      !response.openEdges ||
      !response.nonManifoldEdges ||
      !response.originalOpenEdges ||
      !response.originalNonManifoldEdges
    ) {
      throw new Error(response.error ?? 'Invalid worker response');
    }

    this.format = response.format ?? 'binary';
    this.original = response.original;
    this.repaired = response.repaired;
    this.report = response.report;
    this.originalEdges = {
      open: response.originalOpenEdges,
      nonManifold: response.originalNonManifoldEdges,
    };
    this.repairedEdges = {
      open: response.openEdges,
      nonManifold: response.nonManifoldEdges,
    };
    this.view = 'repaired';
    this.settingsDirty = false;

    this.showCurrentView(true);
    this.renderAllAfterChange();
  }

  private setBusy(on: boolean, key?: string): void {
    this.busy = on;
    if (key) this.busyKey = key;
    this.busyOverlay.classList.toggle('hidden', !on);
    this.busyText.textContent = t(this.busyKey);
    this.updateButtons();
    this.renderRepairStatus();
  }

  // ---------- worker ----------

  private getWorker(): Worker {
    if (this.worker) return this.worker;

    const worker = new Worker(new URL('./repair.worker.ts', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = (event: MessageEvent<RepairResponse>) => {
      const pending = this.pending.get(event.data.id);
      if (!pending) return;
      this.pending.delete(event.data.id);
      if (event.data.ok) pending.resolve(event.data);
      else pending.reject(new Error(event.data.error ?? 'Repair failed'));
    };
    worker.onerror = (event) => {
      const error = new Error(event.message || 'Worker error');
      for (const pending of this.pending.values()) pending.reject(error);
      this.pending.clear();
      worker.terminate();
      this.worker = null;
    };

    this.worker = worker;
    return worker;
  }

  private requestRepair(buffer: ArrayBuffer): Promise<RepairResponse> {
    const worker = this.getWorker();
    const id = ++this.requestSeq;
    const copy = buffer.slice(0);

    return new Promise<RepairResponse>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      worker.postMessage({ id, buffer: copy, options: { ...this.options } }, [copy]);
    });
  }

  // ---------- pliki ----------

  private async loadFile(file: File): Promise<void> {
    if (!/\.stl$/i.test(file.name)) {
      alert(t('alertChooseStl'));
      return;
    }
    if (this.busy) return;

    this.setBusy(true, 'busyLoading');
    try {
      const buffer = await file.arrayBuffer();
      const response = await this.requestRepair(buffer);

      this.fileBuffer = buffer;
      this.fileName = file.name;
      this.fileSize = file.size;
      const base = file.name.replace(/\.stl$/i, '') || 'model';
      this.exportName = `${base}_fixed.stl`;

      this.applyResponse(response);
    } catch (error) {
      console.error(error);
      const message = error instanceof Error ? error.message : String(error);
      alert(`${t('alertLoadFailed')}\n\n${message}`);
    } finally {
      this.setBusy(false);
    }
  }

  private async runRepair(): Promise<void> {
    if (!this.fileBuffer || this.busy) return;

    this.setBusy(true, 'busyRepairing');
    try {
      const response = await this.requestRepair(this.fileBuffer);
      this.applyResponse(response);
    } catch (error) {
      console.error(error);
      const message = error instanceof Error ? error.message : String(error);
      alert(`${t('alertRepairFailed')} ${message}`);
    } finally {
      this.setBusy(false);
    }
  }

  private download(): void {
    if (!this.repaired) return;

    const buffer = writeBinaryStl(this.repaired);
    const blob = new Blob([buffer], { type: 'model/stl' });

    let name = (this.exportName || 'model_fixed.stl').trim();
    name = name.replace(/[\\/:*?"<>|]+/g, '_');
    if (!/\.stl$/i.test(name)) name += '.stl';

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }
}
